import React, { useState, useEffect, useRef, useMemo } from 'react';
// import { appInsights, reactPlugin } from '../../appInsights';
// import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { SelectChangeEvent } from '@mui/material';
import Button from '@mui/material/Button';
import cn from 'clsx';
import { TextComponent, SelectComponent } from '../uiComponents';
import { gritComponentStyles } from './gritComponentStyles';
import { getClaimsDropdownList } from '../../store';
import { useSelector } from 'react-redux';
import { init_PreconditionTP, init_PreconditionOrchestration } from '../../xmlParsing/initInterfaces';
import { Precondition } from '../../xmlParsing/interfaces';
import DeleteIcon from '@mui/icons-material/DeleteSharp';
import isEqual from 'lodash/isEqual';

interface PreConditionsComponentProps {
  data: Precondition[];
  type: 'orchestration' | 'validationTP';
  addHandler: (data: any) => void;
  deleteHandler: (data: any) => void;
  disableSubmit?: (data: any) => void;
}

function PreConditionsComponent({ data, type, addHandler, deleteHandler, disableSubmit }: PreConditionsComponentProps) {
  const origData = useRef<Precondition[]>(data);
  const [showData, setShowData] = useState<Precondition[]>(data);
  const [modifiedData, setModifiedData] = useState<Precondition>(
    type === 'validationTP' ? init_PreconditionTP() : init_PreconditionOrchestration(),
  );
  const classes = gritComponentStyles();
  const allClaimsDropdownList = useSelector(getClaimsDropdownList);

  const dragItem = useRef();
  const dragOverItem = useRef();

  const dragStart = (e: any, position: any) => {
    dragItem.current = position;
  };

  const dragEnter = (e: any, position: any) => {
    dragOverItem.current = position;
  };

  const drop = (e: any) => {
    const copyListItems = [...showData];
    const dragItemContent = copyListItems[dragItem.current || 0];
    copyListItems.splice(dragItem.current || 0, 1);
    copyListItems.splice(dragOverItem.current || 0, 0, dragItemContent);
    dragItem.current = undefined;
    dragOverItem.current = undefined;
    setShowData(copyListItems);
    deleteHandler(copyListItems);
  };

  useEffect(() => {
    if (disableSubmit) isEqual(origData.current, showData) ? disableSubmit(true) : disableSubmit(false);
  }, [showData]);

  useEffect(() => {
    if (allClaimsDropdownList.length > 0) {
      const _values = [allClaimsDropdownList[0]['value']];
      modifiedData['type'] === 'ClaimEquals' && _values.push('');
      setModifiedData({ ...modifiedData, values: _values });
    }
  }, [allClaimsDropdownList]);

  useMemo(() => {
    setShowData(data);
  }, [data]);

  return (
    <>
      <div className={classes.flexCol}>
        {showData.map((condition: Precondition, index: number) => {
          const key = 'condition-' + Math.floor(Math.random() * 100000);
          let c = '';
          if (condition['type'] === 'ClaimsExist') {
            // claimsExist
            c = condition['executeActionsIf']
              ? condition['values'][0] + ' exists'
              : condition['values'][0] + ' does not exist';
          } else {
            // claimEquals
            c = condition['executeActionsIf']
              ? condition['values'][0] + ' == ' + (condition['values'][1] || '""')
              : condition['values'][0] + ' != ' + (condition['values'][1] || '""');
          }
          return (
            <div
              style={{
                backgroundColor: 'lightblue',
                margin: '10px',
                padding: '0 10px',
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onDragStart={(e) => dragStart(e, index)}
              onDragEnter={(e) => dragEnter(e, index)}
              onDragEnd={drop}
              draggable
              key={key}
              className={classes.flexRow}
            >
              <div>{'if (' + c + ' ) ' + condition['action']}</div>
              <DeleteIcon
                sx={{ color: 'red', cursor: 'pointer' }}
                onClick={() => {
                  const a = showData.reduce((acc: Precondition[], curVal: Precondition, ind: number) => {
                    return index !== ind ? acc.concat([curVal]) : acc;
                  }, []);
                  if (window.confirm('Are you sure you want to delete this precondition? This cannot be undone.')) {
                    setShowData(a);
                    deleteHandler(a);
                  }
                }}
              />
            </div>
          );
        })}
      </div>
      {allClaimsDropdownList.length > 0 && (
        <>
          <div className={cn(classes.flexCol, classes.border)}>
            <div className={classes.flexRow}>
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  setModifiedData({
                    ...modifiedData,
                    // @ts-ignore
                    type: e.target.value,
                    values:
                      e.target.value === 'ClaimsExist'
                        ? [allClaimsDropdownList[0]['value']]
                        : [allClaimsDropdownList[0]['value'], ''],
                  });
                }}
                data={{
                  value: modifiedData['type'],
                  label: 'Type',
                  options: [
                    { label: 'ClaimsExist', value: 'ClaimsExist' },
                    { label: 'ClaimEquals', value: 'ClaimEquals' },
                  ],
                }}
              />
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  setModifiedData({
                    ...modifiedData,
                    executeActionsIf: e.target.value == 'true' ? true : false,
                  });
                }}
                data={{
                  value: modifiedData['executeActionsIf'].toString(),
                  label: 'executeActionsIf',
                  options: [
                    { label: 'true', value: 'true' },
                    { label: 'false', value: 'false' },
                  ],
                }}
              />
            </div>
            <div className={classes.flexRow}>
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  if (modifiedData['type'] === 'ClaimsExist')
                    setModifiedData({ ...modifiedData, values: [e.target.value] });
                  else setModifiedData({ ...modifiedData, values: [e.target.value, ''] });
                }}
                data={{
                  value: modifiedData['values'][0] || allClaimsDropdownList[0]['value'],
                  label: 'Claim',
                  options: allClaimsDropdownList,
                }}
              />
              {modifiedData['type'] === 'ClaimEquals' && (
                <TextComponent
                  changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                    const _temp = modifiedData['values'];
                    _temp[1] = e.target.value;
                    setModifiedData({ ...modifiedData, values: _temp });
                  }}
                  data={{
                    value: modifiedData['values'][1],
                    label: 'Equal To',
                  }}
                />
              )}
            </div>
            <footer className={classes.footer}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  addHandler(modifiedData);
                  const _temp = type === 'validationTP' ? init_PreconditionTP() : init_PreconditionOrchestration();
                  const values =
                    _temp['type'] === 'ClaimsExist'
                      ? [allClaimsDropdownList[0]['value']]
                      : [allClaimsDropdownList[0]['value'], ''];
                  setModifiedData({ ..._temp, values: values });
                }}
              >
                Add a Precondition
              </Button>
            </footer>
          </div>
        </>
      )}
    </>
  );
}

// export default withAITracking(reactPlugin, PreConditionsComponent);
export default PreConditionsComponent;
